import { useEffect, useRef } from 'react';

const FOCALISABLES = 'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

export interface OptionsFenetre {
  /** Ce qui reçoit le focus à l'ouverture (sinon le bouton « Fermer » du panneau). */
  focusInitial?: () => HTMLElement | null;
  /**
   * Une autre fenêtre est ouverte PAR-DESSUS celle-ci (fiche FRA, proposition, confirmation…) : elle garde le clavier — Échap la ferme
   * elle, Tab va dans ses boutons. Lu à chaque touche. Sans cela, la fenêtre du dessous, qui a gardé le focus, volerait ces touches.
   */
  auDessus?: () => boolean;
  /** La fenêtre se ferme parce qu'on a cliqué AILLEURS : le focus reste où la personne vient de le mettre, il ne revient pas à l'origine. */
  sansRetourFocus?: () => boolean;
}

/**
 * Une fenêtre par-dessus la page (détail du jour, confirmation…) qu'on peut utiliser au clavier et avec un lecteur d'écran : le focus entre
 * dans la fenêtre à l'ouverture, Tab y reste, Échap la ferme, et le focus revient là où il était (la carte cliquée). Les touches sont
 * écoutées sur le document, pour que la fenêtre réponde aussi quand le focus est tombé sur la page (clic sur du texte, bouton disparu).
 */
export function useFenetreModale(onFermer: () => void, options: OptionsFenetre = {}) {
  const ref = useRef<HTMLDivElement>(null);
  const fermer = useRef(onFermer);
  fermer.current = onFermer;
  const opts = useRef(options);
  opts.current = options;

  useEffect(() => {
    const actif0 = document.activeElement as HTMLElement | null;
    const origine = actif0 && actif0 !== document.body ? actif0 : null;
    const cle = origine?.getAttribute('data-cle') ?? origine?.getAttribute('data-jour') ?? null;
    (opts.current.focusInitial?.() ?? ref.current?.querySelector<HTMLElement>('.panel-slide-close'))?.focus();

    function surTouche(e: KeyboardEvent) {
      const fenetre = ref.current;
      if (!fenetre || opts.current.auDessus?.()) return;
      const actif = document.activeElement as HTMLElement | null;
      const surLaPage = !actif || actif === document.body;
      if (!surLaPage && !fenetre.contains(actif)) return;   // le focus est dans une autre fenêtre : ce n'est pas à nous
      if (e.key === 'Escape') { e.stopPropagation(); e.preventDefault(); fermer.current(); return; }
      if (e.key !== 'Tab') return;
      const el = Array.from(fenetre.querySelectorAll<HTMLElement>(FOCALISABLES)).filter((n) => n.offsetParent !== null);
      if (el.length === 0) return;
      if (surLaPage || (e.shiftKey && actif === el[0]) || (!e.shiftKey && actif === el[el.length - 1])) {
        e.preventDefault();
        (e.shiftKey ? el[el.length - 1] : el[0]).focus();
      }
    }
    document.addEventListener('keydown', surTouche);
    return () => {
      document.removeEventListener('keydown', surTouche);
      if (opts.current.sansRetourFocus?.()) return;
      // Si l'origine a disparu (le bouton cliqué n'existe plus après l'action), le focus revient à la fenêtre restée ouverte dessous.
      const cible = origine?.isConnected ? origine
        : (cle ? document.querySelector<HTMLElement>(`[data-cle="${cle}"], [data-jour="${cle}"]`) : null) ?? document.querySelector<HTMLElement>('.panel-slide-close');
      cible?.focus();
    };
  }, []);

  return { ref };
}
